import { useRef, useState } from "react"
import { EditorModifier, RoleType, StorageKey } from "../../../../App/enums"
import { Editor } from "@tinymce/tinymce-react"
import { useEffect } from "react"
import { replaceHTMLCharactersWithCorrespondingLetters } from "../../../../App/Helpers/Helpers"
import { EditorProps, TagType } from "../../../../Types/EditorTypes"
import { headers } from "../../../../App/Axios"
import HTTPService from "../../../../App/HTTPService"
import useWindowSize from "../../Hooks/useWindowSize"
import { organizationProfileDefault, profileDefault } from "../../../../App/GlobaleVariables"
import StorageService from "../../../../App/StorageService"

function EditorTextArea({
    inputState,
    setInputState,
    enterPressed,
    editorRef,
    editorPlaceholder,
    setContent,
    editorHeight,
    modifier,
    initialEditorValue,
    isChat,
    isUserDashboard
}: EditorProps) {
    const [hasContent, setHasContent] = useState(false)
    const [editorMounted, setEditorMounted] = useState(false)
    const windowSize = useWindowSize()
    const breakpoint = 768
    const isChatEditor = isChat && modifier === EditorModifier.roomTextArea
    const isRoomPostTabEditor = modifier === EditorModifier.roomPostTab
    const isFeedTextAreaEditor = modifier === EditorModifier.feedTextArea
    const chatMinHeight = windowSize >= breakpoint ? 76 : 56
    const chatHeight = windowSize >= breakpoint ? 84 : 64
    const roomPostTabMinHeight = windowSize >= breakpoint ? 176 : 152
    const roomPostTabHeight = windowSize >= breakpoint ? 184 : 160
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900
    const roomPostTabMaxHeight = windowSize >= breakpoint
        ? Math.max(260, Math.floor(viewportHeight * 0.42))
        : Math.max(280, Math.floor(viewportHeight * 0.5))
    const feedTextAreaMinHeight = windowSize >= breakpoint ? 188 : 156
    const feedTextAreaHeight = windowSize >= breakpoint ? 196 : 164
    const roomTextAreaMinHeight = windowSize >= breakpoint ? 150 : 116
    const roomTextAreaHeight = windowSize >= breakpoint ? 150 : 116
    const roomTextAreaMaxHeight = windowSize >= breakpoint ? 210 : 160
    const minHeight = isChatEditor
        ? chatMinHeight
        : isRoomPostTabEditor
            ? roomPostTabMinHeight
        : isFeedTextAreaEditor
            ? feedTextAreaMinHeight
        : modifier === EditorModifier.roomTextArea
            ? roomTextAreaMinHeight
            : modifier === EditorModifier.editRoomDescription
                ? 180
                : modifier === EditorModifier.eventDescription
                    ? 180
                    : modifier === EditorModifier.editContent
                        ? 250
                        : 100
    const height = isChatEditor
        ? chatHeight
        : isRoomPostTabEditor
            ? roomPostTabHeight
        : isFeedTextAreaEditor
            ? feedTextAreaHeight
        : modifier === EditorModifier.roomTextArea
            ? roomTextAreaHeight
            : modifier === EditorModifier.editRoomDescription
                ? 180
                : modifier === EditorModifier.eventDescription
                    ? 180
                    : modifier === EditorModifier.editContent
                        ? 250
                        : 100
    const maxHeightNumber = isChatEditor
        ? (windowSize >= breakpoint ? 180 : 128)
        : isFeedTextAreaEditor
            ? (windowSize >= breakpoint ? 280 : 190)
        : isRoomPostTabEditor
            ? roomPostTabMaxHeight
            : modifier === EditorModifier.roomTextArea
                ? roomTextAreaMaxHeight
                : (windowSize >= breakpoint ? 210 : (modifier === EditorModifier.editRoomDescription ? 180 : 100))
    const lastExternalValueRef = useRef<string>("")
    const editorReadyRef = useRef(false)
    const imageCacheRef = useRef<Record<string, string>>({})
    const isIOS =
        typeof navigator !== "undefined" &&
        (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1))
    const toolbarLocation = isRoomPostTabEditor || isFeedTextAreaEditor
        ? "top"
        : isUserDashboard && isIOS
            ? "top"
            : (isUserDashboard ? "bottom" : "auto")
    const mobileToolbarMode = isChatEditor ? "sliding" : (isIOS ? "sliding" : "floating")
    // Capture initial value once to avoid reapplying content on re-renders
    const initialValueRef = useRef<string>(initialEditorValue || inputState || "")

    const getBase64Image = async (res: Response): Promise<string | ArrayBuffer | null> => {
        const blob = await res.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const loadImageWithAuth = async (imageUrl: string): Promise<string> => {
        // Check cache first
        if (imageCacheRef.current[imageUrl]) {
            return imageCacheRef.current[imageUrl];
        }

        const accessToken = StorageService.getItem(StorageKey.accessToken) as string;
        const isMedia = imageUrl.startsWith("/media") || imageUrl.includes(process.env.REACT_APP_BASE_API_URL!.replace("api", ""));

        // If it's not a media URL or no auth token, return as-is
        if (!isMedia || !accessToken) {
            return imageUrl;
        }

        try {
            let url = imageUrl;
            
            // Normalize dev URLs
            if (url.includes("3000")) {
                url = url.replace("3000", "8000").replace("https://", "http://");
            }

            // Ensure absolute URL for localhost
            if (window.location.hostname === "localhost" && url.startsWith("/media")) {
                url = process.env.REACT_APP_BASE_API_URL!.replace("api", "") + url;
            }

            url = url.replace("//media", "/media");

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                return imageUrl;
            }

            const base64Image = (await getBase64Image(response)) as string;
            imageCacheRef.current[imageUrl] = base64Image;
            return base64Image;
        } catch (error) {
            console.warn("Failed to load image with auth:", imageUrl, error);
            return imageUrl;
        }
    };

    // Sync external value into the editor once it becomes available (e.g., edit mode async fetch)
    // This only runs when the external value changes and differs from the last value we pushed from the editor itself.
    useEffect(() => {
        if (!editorMounted || !editorRef.current) return
        if (inputState === undefined || inputState === null) return
        if (inputState === lastExternalValueRef.current) return

        editorRef.current.setContent(inputState)
        lastExternalValueRef.current = inputState
    }, [inputState, editorMounted, editorRef])

    const debounce = (callback: (arg0: any) => void, wait: number | undefined) => {
        let timeoutId: number | undefined
        return (...args: any) => {
            window.clearTimeout(timeoutId)
            timeoutId = window.setTimeout(() => {
                callback.apply(null, args)
            }, wait)
        }
    }

    const handleOnChange = debounce((e: React.FormEvent<HTMLTextAreaElement>) => {
        if (editorRef.current) {
            if (isChat) {
                let content = editorRef.current.getContent({ format: "raw" })
                setContent!(editorRef!.current!.getContent({ format: "raw" }).length === 0 ? "" : content)!
            } else {
                let content = editorRef.current.getContent({ format: "text" })
                setContent!(editorRef!.current!.getContent().length === 0 ? "" : content)!
            }

            const newContent = replaceHTMLCharactersWithCorrespondingLetters(editorRef.current.getContent())
            setInputState(newContent)
            setHasContent(newContent.trim().length > 0)
            lastExternalValueRef.current = newContent
        }
    }, 150)

    return (
        <div className={`editor-container editor-container--${modifier} ${!hasContent ? 'editor-container--empty' : ''}`}>
            {/* @ts-ignore*/}
            <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                licenseKey="gpl"
                onInit={(evt : any, editor : any) => {
                    editorRef.current = editor
                    editorReadyRef.current = true
                    setEditorMounted(true)
                    
                    // Ensure initial content is applied once on mount (use latest available value)
                    try {
                        if (!initialValueRef.current && (initialEditorValue || inputState)) {
                            initialValueRef.current = initialEditorValue || inputState || ""
                        }
                        const initVal = initialValueRef.current || inputState || initialEditorValue || ""
                        if (initVal && initVal.length > 0) {
                            // Only set if editor is empty to avoid resetting caret on first keystroke
                            if (!editor.getContent({ format: 'raw' })) {
                                editor.setContent(initVal)
                                lastExternalValueRef.current = initVal
                            }
                        }
                    } catch (_) {}
                }}
                initialValue={initialValueRef.current}
                init={{
                    mobile: {
                        toolbar_mode: mobileToolbarMode,
                        toolbar: isChatEditor
                            ? "bold italic emoticons"
                            : "bold italic bullist numlist link emoticons"
                    },
                    toolbar_mode: isChatEditor ? "wrap" : "floating",
                    menubar: false,
                    plugins: isChatEditor
                        ? "autoresize autolink emoticons"
                        : modifier !== EditorModifier.editRoomDescription && modifier !== EditorModifier.eventDescription
                            ? "autoresize lists link autolink emoticons"
                            : "lists emoticons",
                    language: "de",
                    language_url: "/tiny_de.js",
                    block_formats: " Header 1=h1; Header 2=h2; Header 3=h3",
                    text_patterns: [
                        { start: "*", end: "*", format: "italic" },
                        { start: "**", end: "**", format: "bold" },
                        { start: "##", format: "h2" },
                        { start: "###", format: "h3" },
                        { start: "####", format: "h4" },
                        { start: "#####", format: "h5" },
                        { start: "######", format: "h6" },
                        { start: "1. ", cmd: "InsertOrderedList" },
                        { start: "* ", cmd: "InsertUnorderedList" },
                        { start: "- ", cmd: "InsertUnorderedList" },
                    ],
                    inline: false,
                    inline_boundaries: false,
                    toolbar: isChatEditor
                        ? "bold italic emoticons"
                        : "undo redo bold italic alignleft aligncenter alignright alignjustify bullist numlist outdent indent link emoticons",
                    quickbars_selection_toolbar: "",
                    quickbars_insert_toolbar: "",
                    quickbars_image_toolbar: "",
                    statusbar: false,
                    content_style: `${modifier === EditorModifier.editContent
                        ? "body{font-size: 14px;color: #375558;background-color:rgba(247, 247, 247, 0.75);}, html{cursor: text;}"
                        : modifier === EditorModifier.editRoomDescription
                            ? "body{font-size: 14px;color: #375558;background-color: #F4F4F4;border-radius:0.5rem!important;}, html{cursor: text;}"
                            : "body{font-size: 14px;color: #375558;}, html{cursor: text; }"
                        } `,
                    content_css: "/editor-tiny.css",
                    toolbar_location: toolbarLocation,
                    toolbar_sticky: isRoomPostTabEditor || isFeedTextAreaEditor,
                    autoresize_overflow_padding: 5,
                    autoresize_bottom_margin: 5,
                    height: height,
                    min_height: minHeight,
                    max_height: maxHeightNumber,
                    width: "100%",
                    max_width: 1728,
                    valid_elements:
                        "span[class|id|data-type|contenteditable|style],p[align|class|style],ul[align|class|style],ol[align|class|style],li[align|class|style],a[align|class|style|href],strong,em,br",
                    placeholder: editorPlaceholder,
                    max_chars: 2000,
                    relative_urls: false,
                    remove_script_host: true,
                    link_title: false,
                    setup: (editor: any) => {
                        // Autocompleter für @-Mentions
                        if (modifier !== EditorModifier.editRoomDescription && modifier !== EditorModifier.eventDescription) {
                            editor.ui.registry.addAutocompleter('mentions', {
                                trigger: '@',
                                minChars: 3,
                                columns: 1,
                                fetch: function (pattern: string) {
                                    return new Promise((resolve) => {
                                        if (pattern.length === 0) {
                                            resolve([])
                                            return
                                        }
                                        
                                        HTTPService.get(`/notification/tag-options-list/?query=${pattern}`, headers)
                                            .then(async (response) => {
                                                if (response.status === 200) {
                                                    const items = await Promise.all(response.data.map(async (user: TagType) => {
                                                        const imageUrl = user.type === RoleType.USER.toLowerCase()
                                                            ? user.profile_photo?.url_path || profileDefault
                                                            : user.photo?.url_path || organizationProfileDefault
                                                        
                                                        const displayName = user.type === RoleType.USER.toLowerCase() 
                                                            ? `${user.first_name} ${user.last_name}` 
                                                            : user.name
                                                        
                                                        // Load image with auth if needed
                                                        const resolvedImageUrl = await loadImageWithAuth(imageUrl)
                                                        
                                                        return {
                                                            type: 'cardmenuitem',
                                                            value: user.id,
                                                            label: displayName,
                                                            items: [
                                                                {
                                                                    type: 'cardcontainer',
                                                                    direction: 'horizontal',
                                                                    items: [
                                                                        {
                                                                            type: 'cardimage',
                                                                            src: resolvedImageUrl,
                                                                            alt: displayName,
                                                                            classes: ['image','image__profile','image__autocompleter']
                                                                        },
                                                                        {
                                                                            type: 'cardtext',
                                                                            text: displayName,
                                                                            classes: ['text','text__darker-blue'],
                                                                        }
                                                                    ]
                                                                }
                                                            ],
                                                            meta: user
                                                        }
                                                    }))
                                                    resolve(items)
                                                } else {
                                                    resolve([])
                                                }
                                            })
                                            .catch(() => resolve([]))
                                    })
                                },
                                onAction: function (autocompleteApi: any, rng: any, value: string, meta: TagType) {
                                    editor.selection.setRng(rng)
                                    editor.insertContent(
                                        `<span class="mention-span" contenteditable="false" id="${meta.id}" data-type="${meta.type}">${
                                            meta.type === RoleType.USER.toLowerCase()
                                                ? "@" + meta.first_name?.replaceAll(" ", "") + meta.last_name?.replaceAll(" ", "")
                                                : "@" + meta.name?.replaceAll(" ", "")
                                        }</span>&nbsp;`
                                    )
                                    autocompleteApi.hide()
                                }
                            })
                        }

                        if (isChat) {
                            editor.on("keydown", function (e: any) {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    enterPressed(true)
                                    e.preventDefault()
                                }
                            })
                        }

                        if (isFeedTextAreaEditor || isRoomPostTabEditor) {
                            editor.on("keydown", function (e: any) {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                    enterPressed(true)
                                    e.preventDefault()
                                }
                            })
                        }
                    },
                }}
                onEditorChange={handleOnChange}
            />
        </div>
    )
}

export default EditorTextArea
